import { http } from '@/lib/http';
import type { Page } from '@/types/common';
import type {
  Customer,
  CustomerAddress,
  CustomerAddressRole,
  CustomerListRow,
  CustomerStatus,
} from '@/types/customers';

const BASE = '/api/v1/customers';

export interface CustomerListQuery {
  search?: string;
  status?: CustomerStatus;
  page?: number;
  pageSize?: number;
}

export interface CustomerCreatePayload {
  name: string;
  code?: string;
  email?: string;
  phone?: string;
  gstin?: string;
  externalRef?: string;
  status?: CustomerStatus;
  /** Optional ISO 4217 — orders for this customer default to this currency. */
  defaultCurrency?: string;
}

export type CustomerUpdatePayload = Partial<Omit<CustomerCreatePayload, 'code'>>;

export const customersService = {
  list: (query: CustomerListQuery = {}) =>
    http.get<Page<CustomerListRow>>(BASE, { params: query }),
  get: (id: string) => http.get<CustomerListRow>(`${BASE}/${id}`),
  create: (payload: CustomerCreatePayload) =>
    http.post<Customer, CustomerCreatePayload>(BASE, payload),
  update: (id: string, payload: CustomerUpdatePayload) =>
    http.patch<Customer, CustomerUpdatePayload>(`${BASE}/${id}`, payload),
  /** Soft delete — sets status=INACTIVE so historical orders/returns keep resolving. */
  archive: (id: string) => http.delete<void>(`${BASE}/${id}`),
  /** Hard delete — permanently removes. BE-guarded: 409 if it has order/billing history. */
  remove: (id: string) => http.delete<void>(`${BASE}/${id}?hard=true`),

  // ── Addresses (Slice 9) ──────────────────────────────────────────
  listAddresses: (customerId: string) =>
    http.get<CustomerAddress[]>(`${BASE}/${customerId}/addresses`),
  createAddress: (customerId: string, payload: AddressCreatePayload) =>
    http.post<CustomerAddress, AddressCreatePayload>(
      `${BASE}/${customerId}/addresses`,
      payload,
    ),
  updateAddress: (
    customerId: string,
    addressId: string,
    payload: AddressUpdatePayload,
  ) =>
    http.patch<CustomerAddress, AddressUpdatePayload>(
      `${BASE}/${customerId}/addresses/${addressId}`,
      payload,
    ),
  deleteAddress: (customerId: string, addressId: string) =>
    http.delete<void>(`${BASE}/${customerId}/addresses/${addressId}`),
};

export interface AddressCreatePayload {
  label?: string;
  role?: CustomerAddressRole;
  contactName?: string;
  contactPhone?: string;
  gstin?: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode?: string;
  country?: string;
  isDefaultBilling?: boolean;
  isDefaultShipping?: boolean;
}

export type AddressUpdatePayload = Partial<AddressCreatePayload>;
